/**
 * RoleFromUserModal — „Utwórz rolę z uprawnień użytkownika" (B12 ze szkolenia 14.10).
 *
 * Pracujemy na rolach, nie na kopiach ludzi: zamiast przenosić prawa osoby A na osobę B, zbieramy
 * to, co osoba A REALNIE ma w tej firmie (role + zespoły + jej ustawienia), w nową rolę, którą
 * potem przypisuje się komu trzeba (`POST /api/role-templates/from-user`).
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

const ROLE_NAME_MAX = 100;

interface RoleFromUserModalProps {
  open: boolean;
  companyId: string | null;
  user: { id: string; label: string } | null;
  onClose: () => void;
  /** Po utworzeniu ekran przestawia się na nową rolę. */
  onCreated: (roleId: string) => void;
}

interface CreatedResponse {
  roleTemplate: { id: string };
  rulesCreated: number;
  conditionRulesLinked: number;
  menuItemsCopied: number;
  menuSkipped: boolean;
}

export function RoleFromUserModal({
  open,
  companyId,
  user,
  onClose,
  onCreated,
}: RoleFromUserModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user) return;
    setName(
      t('tenant.permissions.center.fromUser.defaultName', { user: user.label }).slice(
        0,
        ROLE_NAME_MAX,
      ),
    );
    setDescription('');
    setError(null);
  }, [open, user, t]);

  const create = useMutation({
    mutationFn: async () => {
      const res = await api.post<CreatedResponse>('/role-templates/from-user', {
        companyId,
        userId: user!.id,
        name: name.trim(),
        description: description.trim() === '' ? undefined : description.trim(),
      });
      return res.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['permissions', 'center', 'roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role-templates'] });
      toast.show({
        variant: 'success',
        title: t('tenant.permissions.center.fromUser.done'),
        description: data.menuSkipped
          ? t('tenant.permissions.center.fromUser.menuSkipped')
          : undefined,
      });
      onCreated(data.roleTemplate.id);
      onClose();
    },
    onError: (err) => {
      const code = isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : undefined;
      setError(
        code === 'NAME_TAKEN'
          ? t('tenant.permissions.center.subjectAdmin.nameTaken')
          : code === 'USER_IS_SUPER_USER'
            ? t('tenant.permissions.center.fromUser.superUser')
            : code === 'SUBJECT_NOT_IN_COMPANY'
              ? t('tenant.permissions.center.fromUser.noAccess')
              : code === 'PERMISSION_DENIED'
                ? t('tenant.permissions.center.noManage')
                : t('tenant.permissions.center.saveError'),
      );
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (name.trim() === '') {
      setError(t('tenant.permissions.center.subjectAdmin.nameRequired'));
      return;
    }
    setError(null);
    create.mutate();
  }

  if (!user) return null;

  const running = create.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      testId="role-from-user-modal"
      title={t('tenant.permissions.center.fromUser.title')}
      description={t('tenant.permissions.center.fromUser.hint', { user: user.label })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={running}>
            {t('common.cancel')}
          </Button>
          <Button form="role-from-user-form" type="submit" loading={running}>
            {t('common.create')}
          </Button>
        </>
      }
    >
      <form id="role-from-user-form" onSubmit={handleSubmit} className="flex flex-col gap-3">
        <FormField
          label={t('tenant.permissions.center.subjectAdmin.name')}
          required
          error={error ?? undefined}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={ROLE_NAME_MAX}
            autoFocus
          />
        </FormField>
        <FormField label={t('tenant.permissions.center.subjectAdmin.description')}>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            rows={3}
          />
        </FormField>
      </form>
    </Modal>
  );
}
